/**
 * THE ARITHMETIC OF SETUP, kept apart from the USB so it can be tested without a printer.
 *
 * Every number the wizard writes to a printer comes out of one of these functions, from something
 * the printer reported or the operator READ OFF PRINTED PAPER. Nothing here is a default tuned on
 * one unit — that is how 626 came to be "the" cut offset for a printer it was never measured on.
 */

import { DOTS_PER_MM } from './constants';

// ── Sensor trace ────────────────────────────────────────────────────────────────────────────────

export interface TraceSample {
  mm: number;
  /** Raw sensor reading, when the firmware reports one (ESC 0xCC 1). NaN when it does not. */
  ad: number;
  /** Status bytes at this position [DLE EOT 4, GS r 1] — the fallback signal. */
  raw?: (number | null)[];
}

export interface TraceAnalysis {
  min: number;
  max: number;
  /** Midpoint between the paper and mark readings. */
  threshold: number;
  /** Marks read darker = higher AD, or lower. Learned from the trace, not assumed. */
  markIsHigh: boolean;
  marks: { startMm: number; endMm: number }[];
  /** Median distance between consecutive mark starts — the ticket length. */
  pitchMm: number | null;
  /** Plain-language verdict for the operator. */
  verdict: 'marks-found' | 'one-mark' | 'no-contrast' | 'no-readings';
}

/** Below this spread the sensor saw one surface the whole way — plain paper, or no sensor reply. */
export const MIN_CONTRAST = 24;

export function analyzeTrace(samples: TraceSample[]): TraceAnalysis {
  const valid = samples.filter((s) => Number.isFinite(s.ad));
  if (valid.length < 4) {
    return { min: 0, max: 0, threshold: 0, markIsHigh: true, marks: [], pitchMm: null, verdict: 'no-readings' };
  }
  const ads = valid.map((s) => s.ad);
  const min = Math.min(...ads);
  const max = Math.max(...ads);
  const threshold = Math.round((min + max) / 2);
  if (max - min < MIN_CONTRAST) {
    return { min, max, threshold, markIsHigh: true, marks: [], pitchMm: null, verdict: 'no-contrast' };
  }
  // The mark is whichever side of the threshold covers LESS of the paper: a mark is a few mm out
  // of every ticket. That decides polarity without knowing the sensor's electrical sense.
  const highCount = ads.filter((a) => a > threshold).length;
  const markIsHigh = highCount <= ads.length - highCount;
  const isMark = (a: number) => (markIsHigh ? a > threshold : a < threshold);

  const marks: { startMm: number; endMm: number }[] = [];
  let start: number | null = null;
  valid.forEach((s, i) => {
    if (isMark(s.ad) && start === null) start = s.mm;
    const last = i === valid.length - 1;
    if (start !== null && (!isMark(s.ad) || last)) {
      marks.push({ startMm: start, endMm: isMark(s.ad) ? s.mm : valid[i - 1].mm });
      start = null;
    }
  });
  // A mark already under the sensor when the trace began has no real START, and one still under it
  // when the trace ended has no real END. Each edge is only trusted where it was actually seen;
  // pitch comes from whichever edge has two or more trustworthy readings.
  const first = valid[0].mm, last = valid[valid.length - 1].mm;
  const starts = marks.filter((m) => m.startMm > first).map((m) => m.startMm);
  const ends = marks.filter((m) => m.endMm < last).map((m) => m.endMm);
  const median = (xs: number[]) => {
    const g = xs.slice(1).map((x, i) => x - xs[i]).sort((a, b) => a - b);
    return g.length ? g[Math.floor(g.length / 2)] : null;
  };
  const pitchMm = median(starts) ?? median(ends);
  return {
    min, max, threshold, markIsHigh, marks, pitchMm,
    verdict: pitchMm !== null ? 'marks-found' : marks.length ? 'one-mark' : 'no-contrast',
  };
}

// ── The ruler slip ──────────────────────────────────────────────────────────────────────────────

/**
 * One ruler line every 3 mm (24 dots), labelled with its distance in mm from the first line.
 * Font A is 24 dots tall, so lines touch without overlapping. The operator reads printed numbers;
 * nobody needs a tape measure, and every answer is already in millimetres.
 */
export const RULER_STEP_MM = 3;
export const RULER_STEP_DOTS = RULER_STEP_MM * DOTS_PER_MM;

export function rulerLabels(lengthMm: number): number[] {
  const n = Math.floor(lengthMm / RULER_STEP_MM) + 1;
  return Array.from({ length: n }, (_, i) => i * RULER_STEP_MM);
}

/**
 * How far the cut has to move to land on the perforation.
 *
 * The ruler's numbers grow towards the TAIL of the paper (later lines leave the head later). So a
 * perforation printed at a higher number than the cut has not reached the blade yet: feed more,
 * raise the offset. Lower number: it went past, lower the offset. With a known pitch the nearer
 * of the two neighbouring perforations wins — moving 4 mm beats moving 146.
 */
export function cutCorrectionMm(cutLabelMm: number, perforationLabelsMm: number[], pitchMm?: number | null): number | null {
  if (!perforationLabelsMm.length) return null;
  const candidates = [...perforationLabelsMm];
  if (pitchMm) for (const p of perforationLabelsMm) candidates.push(p - pitchMm, p + pitchMm);
  return candidates.map((p) => p - cutLabelMm).reduce((best, d) => (Math.abs(d) < Math.abs(best) ? d : best));
}

/** Offset in printer units (0.125 mm = 1 dot), clamped to what the command can carry. */
export const offsetUnitsFromMm = (mm: number) => Math.max(0, Math.min(1600, Math.round(mm * DOTS_PER_MM)));
export const mmFromOffsetUnits = (u: number) => u / DOTS_PER_MM;

/**
 * Where printing starts relative to the perforation above it.
 *
 * The ruler's 0 is the first printed line. If the first perforation on the slip sits at label P,
 * printing started P mm BEFORE it, i.e. `pitch - P` mm after the previous one.
 */
export function printStartAfterPerforationMm(firstPerforationLabelMm: number, pitchMm: number): number {
  const v = (pitchMm - firstPerforationLabelMm) % pitchMm;
  return v < 0 ? v + pitchMm : v;
}

/**
 * Head-to-blade distance from the blade slip. After the last line (label L) the head has advanced
 * one more line, so it sits at L + one step; the blade cut the paper `gap` behind that.
 */
export const cutterGapMm = (lastPrintedLabelMm: number, cutLabelMm: number) => lastPrintedLabelMm + RULER_STEP_MM - cutLabelMm;

// ── Head width probe ────────────────────────────────────────────────────────────────────────────

/**
 * One line of font-A characters (12 dots each) with a letter at the last column of each candidate
 * width. The printer wraps at its real width, so the LAST LETTER ON THE FIRST LINE names the head.
 */
export const WIDTH_CANDIDATES = [
  { letter: 'A', cols: 32, dots: 384, label: '48 mm print (58 mm paper)' },
  { letter: 'B', cols: 48, dots: 576, label: '72 mm print (80 mm paper)' },
  { letter: 'C', cols: 53, dots: 640, label: '80 mm print (82.5 mm paper)' },
  { letter: 'D', cols: 69, dots: 832, label: '104 mm print (112 mm paper)' },
] as const;

export function widthProbeLine(): string {
  const total = WIDTH_CANDIDATES[WIDTH_CANDIDATES.length - 1].cols + 4;
  const chars: string[] = [];
  for (let col = 1; col <= total; col++) {
    const hit = WIDTH_CANDIDATES.find((c) => c.cols === col);
    chars.push(hit ? hit.letter : col % 10 === 0 ? String((col / 10) % 10) : col % 5 === 0 ? ':' : '.');
  }
  return chars.join('');
}

/**
 * WHEN THE HEAD IS WIDER THAN THE PAPER. ✅ FIELD: the EP802's head is 104 mm and the ticket stock is
 * ~80 mm, so the probe line ran off both edges and no letter answered. Two stacked rows — tens over
 * units — let the operator read the exact first and last column that lands ON the paper.
 */
export function edgeProbeLines(cols = 69): [string, string] {
  const tens = Array.from({ length: cols }, (_, i) => String(Math.floor((i + 1) / 10) % 10)).join('');
  const units = Array.from({ length: cols }, (_, i) => String((i + 1) % 10)).join('');
  return [tens, units];
}

/** The paper's position on the head, from the first and last column visible on it (font A = 12 dots). */
export function paperSpanFromColumns(firstCol: number, lastCol: number) {
  const leftDots = Math.max(0, (Math.round(firstCol) - 1) * 12);
  const widthDots = Math.max(12, (Math.round(lastCol) - Math.round(firstCol) + 1) * 12);
  return { leftDots, widthDots };
}

/**
 * WHEN THE SENSOR WON'T REPORT A NUMBER, WATCH THE STATUS BITS INSTEAD. ✅ FIELD: the EP802
 * (APP22-11-11-A5) ignores ESC 0xCC 1, but its status bytes move as the paper does. The bit that
 * flips once per ticket IS the mark/notch sensor; it is found from the data, not assumed.
 */
export const STATUS_TRACE_SOURCES = ['DLE EOT 4', 'GS r 1'] as const;

export function statusTraceSignal(samples: TraceSample[]): { label: string; converted: TraceSample[]; flips: number } | null {
  let best: { src: number; bit: number; flips: number } | null = null;
  for (let src = 0; src < STATUS_TRACE_SOURCES.length; src++) {
    for (let bit = 0; bit < 8; bit++) {
      let flips = 0, prev: boolean | null = null, seenOn = false, seenOff = false;
      for (const s of samples) {
        const b = s.raw?.[src];
        if (b === null || b === undefined) continue;
        const on = !!(b & (1 << bit));
        if (on) seenOn = true; else seenOff = true;
        if (prev !== null && on !== prev) flips++;
        prev = on;
      }
      if (seenOn && seenOff && flips >= 2 && (!best || flips < best.flips || best.flips < 2)) {
        // Prefer the bit that flips LEAST but at least twice: a notch sensor flips twice per ticket;
        // noisier bits flip more.
        if (!best || flips < best.flips) best = { src, bit, flips };
      }
    }
  }
  if (!best) return null;
  const { src, bit, flips } = best;
  return {
    label: `${STATUS_TRACE_SOURCES[src]} bit 0x${(1 << bit).toString(16).padStart(2, '0')}`,
    flips,
    converted: samples.map((s) => ({ mm: s.mm, ad: ((s.raw?.[src] ?? 0) & (1 << bit)) ? 220 : 30 })),
  };
}
